import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, Grid, Grow, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField } from "@mui/material";
import { commonSort, getTitle } from "../../common";
import AddButton from "../../components/AddButton/AddButton";
import SearchBar from "../../components/SearchBar/SearchBar";
import TableButtons from "../../components/Button/TableButtons";
import LoadingBackdrop from "../../components/LoadingBackdrop/LoadingBackdrop";
import Modal from "../../components/Modal/Modal";
import { StyledTableDiv } from "../../components/StyledComponents/StyledComponents";
import {
    getBreakdownReasonDetails,
    addBreakdownReasonDetail,
    updateBreakdownReasonDetail,
    deleteBreakdownReasonDetail,
    getPlants,
    getBreakDownReasonGroups,
    getBreakdownReasonPlaceByGroupId,
} from "../../store/actions/BreakdownReasonDetailActions";

export default function BreakdownReasonDetail() {
    const { t } = useTranslation("translation");

    const dispatch = useDispatch();
    const [initialRows, setInitialRows] = useState([]);
    let [rows, setRows] = useState([]);
    const [pageLoaded, setPageLoaded] = useState(false);

    const { BreakdownReasonDetail } = useSelector((BreakdownReasonDetail) => BreakdownReasonDetail);

    useEffect(() => {
        getPlantCodeHandler();
        getGroupCodeHandler();
        loadHandler();
    }, []);

    useEffect(() => {
        if (BreakdownReasonDetail?.breakdownReasonDetails?.resultObject) {
            setInitialRows(BreakdownReasonDetail.breakdownReasonDetails.resultObject);
            setRows(BreakdownReasonDetail.breakdownReasonDetails.resultObject);
            BreakdownReasonDetail.breakdownReasonDetails = {};
            setPageLoaded(true);
        }
        if (BreakdownReasonDetail?.plants?.resultObject) {
            setPlantList(BreakdownReasonDetail.plants.resultObject);
        }
        if (BreakdownReasonDetail?.isTaskUpdated) {
            setEditableRow(false);
            setDisableButton(false);
            loadHandler();
        }
        if (BreakdownReasonDetail?.breakdownReasonGroups?.resultObject) {
            setGroupList(BreakdownReasonDetail.breakdownReasonGroups.resultObject);
        }
        if (BreakdownReasonDetail?.breakdownReasonPlaces?.resultObject) {
            setPlaceList(BreakdownReasonDetail.breakdownReasonPlaces.resultObject);
        }
    }, [BreakdownReasonDetail]);

    const loadHandler = () => {
        dispatch(getBreakdownReasonDetails());
    };

    // Get plant codes
    const [plantList, setPlantList] = useState([]);
    const getPlantCodeHandler = () => {
        dispatch(getPlants());
    };

    // Get group names
    const [groupList, setGroupList] = useState([]);
    const getGroupCodeHandler = () => {
        dispatch(getBreakDownReasonGroups());
    };

    // Get place names
    const [placeList, setPlaceList] = useState([]);
    const getPlaceCodeHandler = (groupId) => {
        dispatch(getBreakdownReasonPlaceByGroupId(groupId));
    };

    // Set groupID
    const [addGroupID, setAddedGroupID] = useState(0);
    const newGroupID = (groupName) => {
        if (groupName != null) {
            const filteredGroup = groupList.filter((item) => item.breakdownReasonGroupLocalizationName === groupName || item.name === groupName);
            getPlaceCodeHandler(filteredGroup[0].id);
            setAddedGroupID(filteredGroup[0].id);
            return filteredGroup[0].id;
        } else {
            setAddedGroupID(0);
        }
    };

    // Set placeID
    const [addPlaceID, setAddedPlaceID] = useState(0);
    const newPlaceID = (placeName) => {
        if (placeName != null) {
            const filteredPlace = placeList.filter((item) => item.name.includes(placeName));
            setAddedPlaceID(filteredPlace[0].id);
            return filteredPlace[0].id;
        } else {
            setAddedPlaceID(0);
        }
    };

    // Update and Add in one function
    const updateAddHandler = (row) => {
        // Update
        if (row.id !== "") {
            let data = {
                id: row.id,
                code: row.code,
                name: row.name,
                breakdownReasonDetailLocalizationName: row.breakdownReasonDetailLocalizationName,
                status: 1,
                plantId: row.plantId,
                breakdownReasonPlaceId: addPlaceID,
            };
            dispatch(updateBreakdownReasonDetail(data));
        }
        // Add
        else {
            let data = {
                code: row.code,
                name: row.name,
                breakdownReasonDetailLocalizationName: row.breakdownReasonDetailLocalizationName,
                plantId: addPlantID,
                breakdownReasonPlaceId: addPlaceID == null || addPlaceID == "" ? 0 : addPlaceID,
            };
            dispatch(addBreakdownReasonDetail(data));
        }
    };

    // Delete row
    const deleteHandler = (row) => {
        toggleModal();
        const deletePlantID = plantList.filter((item) => item.code === row.plantCode)[0].id;
        let data = {
            id: row.id,
            code: row.code,
            name: row.name,
            breakdownReasonDetailLocalizationName: row.breakdownReasonDetailLocalizationName,
            plantId: deletePlantID,
            breakdownReasonPlaceId: row.breakdownReasonPlaceId,
        };
        dispatch(deleteBreakdownReasonDetail(data));
    };

    // Add blank row for new plant addition
    const addBlankRowHandler = () => {
        setAddedGroupID(0);
        setAddedPlaceID(0);
        setAddedPlantID(0);
        setCurrentPage(0);
        setDisableButton(true);
        let addedBlankRow = [
            {
                id: "",
                code: "",
                name: "",
                plantCode: "",
                groupCode: "",
                breakdownReasonDetailLocalizationName: "",
            },
        ];
        let newRows = addedBlankRow.concat(rows);
        setEditableRow(addedBlankRow[0].id);
        setEditable(true);
        setRows(newRows);

        setError({
            breakdownReasonGroupText: t("SelectABreakdownReasonGroup"),
            breakdownReasonPlaceText: t("SelectABreakdownReasonLocationDescription"),
            breakdownReasonDetailCodeText: t("EmptyCheck"),
            breakdownReasonDetailNameText: t("EmptyCheck"),
            plantText: t("SelectAPlant"),
        });
    };

    // Set plantID
    let [addPlantID, setAddedPlantID] = useState(0);
    const newPlantID = (plantCode) => {
        if (plantCode != null) {
            const filteredPlant = plantList.filter((item) => item.code === plantCode);
            setAddedPlantID(filteredPlant[0].id);
            return [filteredPlant[0].id, filteredPlant[0].name];
        } else {
            setAddedPlantID(0);
        }
    };

    // Filter table
    const [filterText, setFilterText] = useState("");
    const filterData = (value) => {
        setFilterText(value);
        const newFilteredText = value.toLowerCase();
        let filteredItems = cloneObject(initialRows).filter(
            (item) =>
                item.breakdownReasonGroupName.toLowerCase().includes(newFilteredText) ||
                item.breakdownReasonName.toLowerCase().includes(newFilteredText) ||
                item.code.toLowerCase().includes(newFilteredText) ||
                item.name.toLowerCase().includes(newFilteredText)
        );
        setRows(filteredItems);
        setDisableButton(false);
    };

    useEffect(() => {
        filterData(filterText);
    }, [initialRows]);

    // Sort function
    let [sortOrder, setSortOrder] = useState();
    let [sortColumn, setSortColumn] = useState();
    const handleSort = (row, column) => {
        const sortedList = commonSort(row, column);
        setSortOrder(sortedList[0]);
        setSortColumn(sortedList[1]);
        setInitialRows(sortedList[2]);
    };

    // Pagination
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const handleChangePage = (_event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setCurrentPage(0);
    };

    // Set edited rows
    const setRowHandler = (value, obj, updatedRow) => {
        const index = rows.indexOf(updatedRow);
        if (updatedRow.id !== "") {
            const row = rows[index];
            row[obj] = value;
            rows[index] = row;
            setRows(rows);
        } else {
            const row = rows[index];
            row[obj] = value;
            setRows(rows);
        }

        // Validation
        if (value.length > 0) {
            if (obj === "code") {
                setError({ ...error, breakdownReasonDetailCodeText: "" });
            } else if (obj === "name") {
                setError({ ...error, breakdownReasonDetailNameText: "" });
            }
        } else {
            if (obj === "code") {
                setError({ ...error, breakdownReasonDetailCodeText: t("EmptyCheck") });
            } else if (obj === "name") {
                setError({ ...error, breakdownReasonDetailNameText: t("EmptyCheck") });
            }
        }
    };

    const cloneObject = (obj) => {
        return JSON.parse(JSON.stringify(obj));
    };

    // Edit rows
    const [editable, setEditable] = useState(false);
    const [editableRow, setEditableRow] = useState("");
    const enableEditHandler = (row) => {
        if (disableButton || editable) {
            filterData(filterText);
        }
        setAddedGroupID(row.breakdownReasonGroupCode);
        setAddedPlaceID(row.breakdownReasonPlaceId);
        setAddedPlantID(row.plantId);
        setEditableRow(row.id);
        setEditable(true);

        setError({
            breakdownReasonGroupText: "",
            breakdownReasonPlaceText: "",
            breakdownReasonDetailCodeText: "",
            breakdownReasonDetailNameText: "",
            plantText: "",
        });
    };

    // Enable disable add button
    const [disableButton, setDisableButton] = useState(false);
    const disableEditHandler = () => {
        filterData(filterText);
        setEditable(false);
        setDisableButton(false);

        setError({
            ...error,
            breakdownReasonDetailCodeText: t("EmptyCheck"),
            breakdownReasonDetailNameText: t("EmptyCheck"),
            plantText: t("SelectAPlant"),
        });
    };

    // Delete Modal
    const [isModalOpen, setModalOpen] = useState(false);
    const [deletedRow, setDeletedRow] = useState([]);
    const deleteButtonHandler = (row) => {
        setDeletedRow(row);
        toggleModal();
    };

    const toggleModal = () => {
        setModalOpen((prevState) => !prevState);
    };

    const renderTableCell = (rowItems, isSortingAction = true) => {
        const directionType = sortOrder === 1 ? "asc" : "desc";
        return rowItems.map((item, index) => {
            if (item.id === "Actions") {
                return (
                    <TableCell id={`brd_rowItems_TableCell_Actions_${index}_${item.id}`} align="center">
                        {t("Actions")}
                    </TableCell>
                );
            } else if (item.id === "") {
                return <TableCell id={`brd_rowItems_TableCell_${index}_${item.id}`} align="center"></TableCell>;
            } else {
                return isSortingAction ? (
                    <TableCell id={`brd_rowItems_TableCell_${index}_${item.id}`} align="center">
                        <TableSortLabel
                            id={`brd_rowItems_TableSortLabel_${index}_${item.id}`}
                            active={sortColumn === item.id}
                            direction={directionType}
                            onClick={() => handleSort(cloneObject(initialRows), item.id)}
                            style={{ paddingLeft: 25 }}
                        >
                            {item.label}
                        </TableSortLabel>
                    </TableCell>
                ) : (
                    <TableCell align="center">{item.label}</TableCell>
                );
            }
        });
    };

    // Validation
    const [error, setError] = useState({
        breakdownReasonGroupText: t("SelectABreakdownReasonGroup"),
        breakdownReasonPlaceText: t("SelectABreakdownReasonLocationDescription"),
        breakdownReasonDetailCodeText: t("EmptyCheck"),
        breakdownReasonDetailNameText: t("EmptyCheck"),
        plantText: t("SelectAPlant"),
    });
    const [isAddDisable, setIsAddDisable] = useState(true);

    useEffect(() => {
        const newBreakdownReasonGroupText = addGroupID ? "" : t("SelectABreakdownReasonGroup");
        const newBreakdownReasonPlaceText = addPlaceID ? "" : t("SelectABreakdownReasonLocationDescription");
        const newPlantText = addPlantID ? "" : t("SelectAPlant");

        setError({
            ...error,
            breakdownReasonGroupText: newBreakdownReasonGroupText,
            breakdownReasonPlaceText: newBreakdownReasonPlaceText,
            plantText: newPlantText,
        });
    }, [addGroupID, addPlaceID, addPlantID]);

    useEffect(() => {
        if (
            error.breakdownReasonGroupText === "" &&
            error.breakdownReasonPlaceText === "" &&
            error.breakdownReasonDetailCodeText === "" &&
            error.breakdownReasonDetailNameText === "" &&
            error.plantText === ""
        ) {
            setIsAddDisable(false);
        } else {
            setIsAddDisable(true);
        }
    }, [error]);

    return (
        <StyledTableDiv>
            {getTitle(t("BreakdownDetail") + " " + t("List"))}
            <AddButton onClick={() => addBlankRowHandler()} disabled={disableButton} />
            {pageLoaded === true ? (
                <Grid container spacing={4} alignItems="center" justifyContent="center" sx={{ marginBottom: 4 }}>
                    <Grid item xs={12} sm={12} md={4}>
                        <SearchBar value={filterText} onChange={(newValue) => setFilterText(newValue)} onRequestSearch={(value) => filterData(value)} />
                    </Grid>
                </Grid>
            ) : null}
            <Box m={2} />
            {rows.length > 0 ? (
                <Grow in={true} style={{ transformOrigin: "0 0 0" }} {...{ timeout: 1000 }}>
                    <TableContainer id="brd_TableContainer" component={Paper}>
                        <Table id="brd_Table" aria-label="customized table">
                            <TableHead id="brd_TableHead">
                                <TableRow id="brd_TableRow">
                                    {renderTableCell([
                                        { id: "id", label: t("ID") },
                                        { id: "breakdownReasonGroupName", label: t("BreakdownGroup") },
                                        { id: "breakdownReasonName", label: t("BreakdownPlace") },
                                        { id: "code", label: t("BreakdownDetailCode") },
                                        { id: "name", label: t("BreakdownDetailName") },
                                        { id: "breakdownReasonDetailLocalizationName", label: t("BreakdownDetailNameLocalized") },
                                        { id: "plantCode", label: t("PlantCode") },
                                        { id: "Actions", label: t("Actions") },
                                    ])}
                                </TableRow>
                            </TableHead>
                            <TableBody id="brd_TableBody">
                                {rows.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage).map((row, index) => (
                                    <TableRow id={`brd_TableRow_${index}`} key={row.id}>
                                        <TableCell id={`brd_TableCell_${index}`} align="center">
                                            {row.id}
                                        </TableCell>
                                        <TableCell id={`brd_TableCell_1_${index}`} align="center">
                                            {editable && editableRow === row.id ? (
                                                <Autocomplete
                                                    id={`brd_TableCell_1_Autocomplete_${index}`}
                                                    aria-required={true}
                                                    defaultValue={row.breakdownReasonGroupName}
                                                    onChange={(event, newValue) => {
                                                        newGroupID(newValue);
                                                    }}
                                                    options={groupList.map((option) => option.breakdownReasonGroupLocalizationName ?? option.name)}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            id={`brd_TableCell_1_TextField_${index}`}
                                                            {...params}
                                                            margin="normal"
                                                            variant="outlined"
                                                            error={error.breakdownReasonGroupText.length > 0}
                                                            helperText={error.breakdownReasonGroupText}
                                                        />
                                                    )}
                                                />
                                            ) : (
                                                row.breakdownReasonGroupName
                                            )}
                                        </TableCell>
                                        <TableCell id={`brd_TableCell_2_${index}`} align="center">
                                            {editable && editableRow === row.id ? (
                                                <Autocomplete
                                                    id={`brd_TableCell_2_Autocomplete_${index}`}
                                                    aria-required={true}
                                                    defaultValue={row.breakdownReasonName}
                                                    onChange={(event, newValue) => {
                                                        newPlaceID(newValue);
                                                    }}
                                                    options={placeList.map((option) => option.name)}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            id={`brd_TableCell_2_TextField_${index}`}
                                                            {...params}
                                                            margin="normal"
                                                            variant="outlined"
                                                            error={error.breakdownReasonPlaceText.length > 0}
                                                            helperText={error.breakdownReasonPlaceText}
                                                        />
                                                    )}
                                                />
                                            ) : (
                                                row.breakdownReasonName
                                            )}
                                        </TableCell>
                                        <TableCell id={`brd_TableCell_3_${index}`} align="center">
                                            {editable && editableRow === row.id ? (
                                                <TextField
                                                    id={`brd_TableCell_3_TextField_${index}`}
                                                    onInput={(e) => setRowHandler(e.target.value, "code", row)}
                                                    defaultValue={row.code}
                                                    variant="outlined"
                                                    error={error.breakdownReasonDetailCodeText.length > 0}
                                                    helperText={error.breakdownReasonDetailCodeText}
                                                />
                                            ) : (
                                                row.code
                                            )}
                                        </TableCell>
                                        <TableCell id={`brd_TableCell_4_${index}`} align="center">
                                            {editable && editableRow === row.id ? (
                                                <TextField
                                                    id={`brd_TableCell_4_TextField_${index}`}
                                                    onInput={(e) => setRowHandler(e.target.value, "name", row)}
                                                    defaultValue={row.name}
                                                    variant="outlined"
                                                    error={error.breakdownReasonDetailNameText.length > 0}
                                                    helperText={error.breakdownReasonDetailNameText}
                                                />
                                            ) : (
                                                row.name
                                            )}
                                        </TableCell>
                                        <TableCell id={`brd_TableCell_5_${index}`} align="center">
                                            {row.breakdownReasonDetailLocalizationName}
                                        </TableCell>
                                        <TableCell id={`brd_TableCell_6_${index}`} align="center">
                                            {editable && editableRow === row.id && editableRow === "" ? (
                                                <Autocomplete
                                                    id={`brd_TableCell_6_Autocomplete_${index}`}
                                                    aria-required={true}
                                                    defaultValue={row.plantCode}
                                                    onChange={(event, newValue) => {
                                                        newPlantID(newValue);
                                                    }}
                                                    options={plantList.map((option) => option.code)}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            id={`brd_TableCell_6_TextField_${index}`}
                                                            {...params}
                                                            margin="normal"
                                                            variant="outlined"
                                                            error={error.plantText.length > 0}
                                                            helperText={error.plantText}
                                                        />
                                                    )}
                                                />
                                            ) : (
                                                row.plantCode
                                            )}
                                        </TableCell>
                                        <TableButtons
                                            addDisabled={isAddDisable}
                                            editable={editable}
                                            editableRow={editableRow}
                                            row={row}
                                            updateAddHandler={() => updateAddHandler(row)}
                                            disableEditHandler={() => disableEditHandler()}
                                            enableEditHandler={() => enableEditHandler(row)}
                                            deleteButtonHandler={() => deleteButtonHandler(row)}
                                            detail={false}
                                        />
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            id="brd_TablePagination"
                            rowsPerPageOptions={[20, 50, 100]}
                            component="div"
                            count={rows.length}
                            rowsPerPage={rowsPerPage}
                            page={currentPage}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </TableContainer>
                </Grow>
            ) : null}

            <Modal title={t("Delete")} content={t("WillYouDoTransaction")} approved={() => deleteHandler(deletedRow)} isModalOpen={isModalOpen} toggle={toggleModal} />

            <LoadingBackdrop />
        </StyledTableDiv>
    );
}
